import type { MetricEntrySeedInput } from './metric-registry';

/**
 * #5544 shrink-on-touch: the pre-existing SG&A lever sub-module + module
 * aggregate registry block, relocated VERBATIM out of metric-registry.ts (was
 * lines 2181-2430, the "sga-spend lever sub-modules - 17 keys" and "sga-spend
 * module aggregate - 13 keys" sections) so that file ends smaller despite this
 * ticket's own additions, per the AMEND §8 instruction: "Put the SG&A
 * definition/taxonomy input data in a focused leaf and map it through the
 * existing registry construction authority... use an existing module export or
 * offsetting extraction."
 *
 * Same seed-input/`.map(metricEntry)` split `metric-registry.ma-synergy-tracking.data.ts`
 * established (`metricEntry` stays module-private to metric-registry.ts, so this
 * leaf exports raw inputs rather than calling it). The one entry that used the
 * also-private `components()` helper (`sga_spend_scenario_reduction_usd`) has
 * that helper's result inlined as a literal - `components(['sga_spend_scenario_base_usd'], [])`
 * is exactly `{ numerator: [{ metricKey: 'sga_spend_scenario_base_usd', temporalType: 'flow' }], denominator: [] }`
 * by that helper's own definition, reproduced below.
 *
 * NOT ONE VALUE CHANGED - every metricKey, label, definition, reconciliationRule,
 * source, unit, direction and sourceBindingKeys is byte-identical to what it was
 * in metric-registry.ts. `apps/api/src/semantic-layer/sga-spend-existing-block-digest-parity.spec.ts`
 * proves this by recomputing every entry's governed definition digest against
 * the digest each carried before the move and asserting zero drift - the
 * ACTIVE-row parity receipt this relocation owes, distinct from and additional
 * to the receipt for #5544's genuinely NEW sga_cost_pool definitions.
 */
const SGA_SPEND_LEVER_SUBMODULE_SEED_INPUTS: readonly MetricEntrySeedInput[] =
  // --- sga-spend lever sub-modules — 17 keys, 0/17 previously compiled -------------
  // NAMING-MISMATCH FLAG (see docs/data/helix-retail-modeled-pack-20260828.md open
  // questions): these `*_opportunity_usd` / `*_addressable_share_pct` keys are the
  // module's OWN per-lever formula outputs (sga-spend-engine.ts, `formulaPrefix`
  // keyed). The compiled registry separately already carries a same-topic sibling
  // for six of the seven levers under a DIFFERENT suffix convention:
  // `<lever>_operating_income_contribution_usd` (see FINANCE_SEMANTIC_LAYER_REGISTRY_SEED
  // in metric-registry.ts, e.g. `marketing_spend_operating_income_contribution_usd`).
  // This does NOT alias one to the other — a person needs to rule whether these
  // are the same concept renamed or genuinely different metrics (a
  // base/addressable/reduction breakdown vs. a single rolled-up contribution figure).
  (
    [
      ['indirect_procurement', 'Indirect procurement'],
      ['marketing_spend', 'Marketing spend'],
      ['outside_contractors', 'Outside contractors'],
      ['professional_fees', 'Professional fees'],
      ['real_estate_footprint', 'Real estate footprint'],
      ['shared_services', 'Shared services'],
      ['tech_software', 'Tech & software'],
      ['travel_and_expense', 'Travel & expense'],
    ] as const
  ).flatMap(([prefix, label]) => {
    const entries: MetricEntrySeedInput[] = [
      {
        metricKey: `${prefix}_opportunity_usd`,
        // #5734 measure-identity sweep: identical across all 8 levers — a
        // modeled SG&A reduction-opportunity dollar amount, never a static
        // string literal to hang a per-key declaration on directly.
        measureIdentity: {
          measuredDimension: 'operating_expense',
          valueForm: 'amount',
          unitFamily: 'currency',
          normalizationBasis: 'none',
        },
        label: `${label}: reduction opportunity`,
        definition: `Governed SG&A reduction opportunity the ${label.toLowerCase()} lever publishes from its addressable base, savings rate, and recovery factor.`,
        reconciliationRule: `Recomputed by the ${prefix} lever formula in sga-spend-engine.ts (formulaPrefix "${prefix}"); rolls into sga_spend_total_opportunity_usd exactly once.`,
        source: `apps/api/src/decision-modules/sga-spend/sga-spend-engine.ts#formulaPrefix:${prefix}_opportunity_usd`,
        ownerModuleId: 'sga-spend',
        unit: 'USD',
        direction: 'higher_is_better',
        sourceOfTruthClass: 'derived',
        sourceBindingKeys: [`${prefix}_opportunity_usd`],
        targetMetricRef: { metricId: 'sga_spend_total_opportunity_usd', registryVersion: 'metric_registry_v1' },
        benchmarkable: false,
      },
    ];
    // tech_software and travel_and_expense declare a different second key
    // (program_cost_usd / policy_exception_rate_pct) than the other six
    // (addressable_share_pct) — matches missing-registry-keys.md exactly.
    if (prefix === 'tech_software') {
      entries.push(
        {
          metricKey: 'tech_software_addressable_share_pct',
          // measuredDimension 'other' pending pool-total normalization basis — see #5844
          measureIdentity: { measuredDimension: 'other', valueForm: 'ratio', unitFamily: 'percent', normalizationBasis: 'other' },
          label: 'Tech & software: addressable share',
          definition: 'Share of total tech & software spend the lever classifies as addressable.',
          reconciliationRule: 'Derived ratio: addressable spend over total spend for the tech_software lever.',
          source: 'apps/api/src/decision-modules/sga-spend/sga-spend-engine.ts#formulaPrefix:tech_software_addressable_share_pct',
          ownerModuleId: 'sga-spend',
          unit: 'percent',
          frame: 'addressable_share_pct',
          temporalType: 'ratio',
          direction: 'context',
          blendPolicy: { kind: 'never_blend' },
          sourceOfTruthClass: 'derived',
          sourceBindingKeys: ['tech_software_addressable_share_pct'],
          benchmarkable: false,
        },
        {
          metricKey: 'tech_software_program_cost_usd',
          measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
          label: 'Tech & software: program cost',
          definition: 'Governed program cost to realize the tech & software lever opportunity.',
          reconciliationRule: 'Direct profile-mapped line from the tech_software lever governed evidence.',
          source: 'apps/api/src/decision-modules/sga-spend/sga-spend-engine.ts#formulaPrefix:tech_software_program_cost_usd',
          ownerModuleId: 'sga-spend',
          unit: 'USD',
          direction: 'lower_is_better',
          sourceOfTruthClass: 'derived',
          sourceBindingKeys: ['tech_software_program_cost_usd'],
          benchmarkable: false,
        },
      );
    } else if (prefix === 'travel_and_expense') {
      entries.push({
        metricKey: 'travel_and_expense_policy_exception_rate_pct',
        measureIdentity: { measuredDimension: 'other', valueForm: 'rate', unitFamily: 'percent', normalizationBasis: 'other' },
        label: 'Travel & expense: policy exception rate',
        definition: 'Share of travel & expense transactions flagged as a policy exception.',
        reconciliationRule: 'Direct profile-mapped rate from the travel_and_expense lever governed evidence.',
        source: 'apps/api/src/decision-modules/sga-spend/sga-spend-engine.ts#formulaPrefix:travel_and_expense_policy_exception_rate_pct',
        ownerModuleId: 'sga-spend',
        unit: 'percent',
        frame: 'policy_exception_rate_pct',
        temporalType: 'ratio',
        direction: 'lower_is_better',
        blendPolicy: { kind: 'never_blend' },
        sourceOfTruthClass: 'derived',
        sourceBindingKeys: ['travel_and_expense_policy_exception_rate_pct'],
        benchmarkable: false,
      });
    } else {
      entries.push({
        metricKey: `${prefix}_addressable_share_pct`,
        // #5734 measure-identity sweep: identical across the 6 levers using this
        // branch — a share-of-category-spend ratio, never a static string
        // literal to hang a per-key declaration on directly.
        measureIdentity: {
          // measuredDimension 'other' pending pool-total normalization basis — see #5844
          measuredDimension: 'other',
          valueForm: 'ratio',
          unitFamily: 'percent',
          normalizationBasis: 'other',
        },
        label: `${label}: addressable share`,
        definition: `Share of total ${label.toLowerCase()} spend the lever classifies as addressable.`,
        reconciliationRule: `Derived ratio: addressable spend over total spend for the ${prefix} lever.`,
        source: `apps/api/src/decision-modules/sga-spend/sga-spend-engine.ts#formulaPrefix:${prefix}_addressable_share_pct`,
        ownerModuleId: 'sga-spend',
        unit: 'percent',
        frame: 'addressable_share_pct',
        temporalType: 'ratio',
        direction: 'context',
        blendPolicy: { kind: 'never_blend' },
        sourceOfTruthClass: 'derived',
        sourceBindingKeys: [`${prefix}_addressable_share_pct`],
        benchmarkable: false,
      });
    }
    return entries;
  });

// --- sga-spend module aggregate — 13 keys, 0/13 previously compiled -------------
const SGA_SPEND_MODULE_AGGREGATE_SEED_INPUTS: readonly MetricEntrySeedInput[] = [
  {
    metricKey: 'sga_spend_addressability_total_spend_usd',
    measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'SG&A: total spend in scope',
    definition: 'Sum of total spend across all approved SG&A levers for the reporting cut.',
    reconciliationRule: 'Sum-of-parts across the 8 approved lever governed snapshots.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend-engine.ts#metricKey:sga_spend_addressability_total_spend_usd',
    ownerModuleId: 'sga-spend',
    unit: 'USD',
    direction: 'context',
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_addressability_total_spend_usd'],
    benchmarkable: false,
  },
  {
    metricKey: 'sga_spend_addressability_total_addressable_spend_usd',
    measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'SG&A: total addressable spend',
    definition: 'Sum of addressable spend across all approved SG&A levers for the reporting cut.',
    reconciliationRule: 'Sum-of-parts across the 8 approved lever governed snapshots.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend-engine.ts#metricKey:sga_spend_addressability_total_addressable_spend_usd',
    ownerModuleId: 'sga-spend',
    unit: 'USD',
    direction: 'context',
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_addressability_total_addressable_spend_usd'],
    benchmarkable: false,
  },
  {
    metricKey: 'sga_spend_objective_gap_usd',
    measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'SG&A: objective gap',
    definition: 'Gap between the governed SG&A objective floor and the certified ranked opportunity.',
    reconciliationRule: 'Derived from the approved governedFloor objective (objectiveFloor.gapUsd) in sga-spend.value-bridge-floor.ts.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend.value-bridge-floor.ts#metricKey:sga_spend_objective_gap_usd',
    ownerModuleId: 'sga-spend',
    unit: 'USD',
    direction: 'lower_is_better',
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_objective_gap_usd'],
    benchmarkable: false,
  },
  {
    metricKey: 'sga_spend_ranked_opportunity_usd',
    measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'SG&A: ranked opportunity',
    definition: 'Certified SG&A opportunity ranked and rolled up across the 8 approved levers.',
    reconciliationRule: 'Recomputed sum-of-parts across per-lever opportunity figures, deconflicted for overlap.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend.governed-emission.ts#SGA_SPEND_RANKED_TOTAL_PLACEHOLDER',
    ownerModuleId: 'sga-spend',
    unit: 'USD',
    direction: 'higher_is_better',
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_ranked_opportunity_usd'],
    benchmarkable: false,
  },
  {
    metricKey: 'sga_spend_scenario_base_usd',
    measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'SG&A scenario: base',
    definition: 'Baseline SG&A spend the what-if reduction scenario models against.',
    reconciliationRule: 'Direct read of the approved SG&A baseline for the scenario reporting cut.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend.scenario-set.ts#metricKey:sga_spend_scenario_base_usd',
    ownerModuleId: 'sga-spend',
    unit: 'USD',
    direction: 'context',
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_scenario_base_usd'],
    benchmarkable: false,
  },
  {
    metricKey: 'sga_spend_scenario_reduction_input_pct',
    // measuredDimension 'other' pending pool-total normalization basis — see #5844
    measureIdentity: { measuredDimension: 'other', valueForm: 'rate', unitFamily: 'percent', normalizationBasis: 'none' },
    label: 'SG&A scenario: reduction input',
    definition: 'User-specified percentage reduction input driving the SG&A what-if scenario.',
    reconciliationRule: 'Server-validated scenario parameter, echoed back on the governed scenario run.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend.scenario-set.ts#metricKey:sga_spend_scenario_reduction_input_pct',
    ownerModuleId: 'sga-spend',
    unit: 'percent',
    frame: 'scenario_input_pct',
    temporalType: 'ratio',
    direction: 'context',
    blendPolicy: { kind: 'never_blend' },
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_scenario_reduction_input_pct'],
    benchmarkable: false,
  },
  {
    metricKey: 'sga_spend_scenario_reduction_usd',
    measureIdentity: { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
    label: 'SG&A scenario: reduction',
    definition: 'Dollar SG&A reduction the what-if scenario computes from the base and the reduction input.',
    reconciliationRule: 'Derived: sga_spend_scenario_base_usd times sga_spend_scenario_reduction_input_pct.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend.scenario-set.ts#metricKey:sga_spend_scenario_reduction_usd',
    ownerModuleId: 'sga-spend',
    unit: 'USD',
    direction: 'higher_is_better',
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_scenario_reduction_usd'],
    // components(['sga_spend_scenario_base_usd'], []) inlined - see file header.
    components: {
      numerator: [{ metricKey: 'sga_spend_scenario_base_usd', temporalType: 'flow' }],
      denominator: [],
    },
    benchmarkable: false,
  },
  ...(
    [
      // #5734 measure-identity sweep: 4th tuple element carries the governed
      // identity per key — template-generated rows have no static
      // `metricKey: '...'` literal to hang a per-key declaration on directly.
      // net_oi_opportunity is explicitly framed as a NET operating-income figure
      // (post program-cost), distinct from its gross/base/reduction SG&A siblings.
      ['sga_spend_tech_software_gross_opportunity_usd', 'Tech & software: gross opportunity', 'higher_is_better' as const,
        { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' }] as const,
      ['sga_spend_tech_software_net_oi_opportunity_usd', 'Tech & software: net OI opportunity', 'higher_is_better' as const,
        { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' }] as const,
      ['sga_spend_tech_software_reduction_base_usd', 'Tech & software: reduction base', 'context' as const,
        { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' }] as const,
      ['sga_spend_tech_software_reduction_usd', 'Tech & software: reduction', 'higher_is_better' as const,
        { measuredDimension: 'operating_expense', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' }] as const,
      ['sga_spend_tech_software_remaining_gross_gap_usd', 'Tech & software: remaining gross gap', 'lower_is_better' as const,
        { measuredDimension: 'operating_expense', valueForm: 'change', unitFamily: 'currency', normalizationBasis: 'none' }] as const,
    ] as const
  ).map(([metricKey, label, direction, measureIdentity]) => ({
    metricKey,
    label: `SG&A ${label}`,
    definition: `Governed tech & software program figure (${label.toLowerCase()}) the sga-spend module publishes for its tech_software lever program.`,
    reconciliationRule: 'Recomputed by the tech_software lever program formulas in sga-spend.tech-software-emission.ts.',
    source: `apps/api/src/decision-modules/sga-spend/sga-spend.tech-software-emission.ts#metricKey:${metricKey}`,
    ownerModuleId: 'sga-spend',
    unit: 'USD',
    direction,
    sourceOfTruthClass: 'derived' as const,
    sourceBindingKeys: [metricKey],
    benchmarkable: false as const,
    measureIdentity,
  })),
  {
    metricKey: 'sga_spend_tech_software_reduction_input_pct',
    // measuredDimension 'other' pending pool-total normalization basis — see #5844
    measureIdentity: { measuredDimension: 'other', valueForm: 'rate', unitFamily: 'percent', normalizationBasis: 'none' },
    label: 'SG&A tech & software: reduction input',
    definition: 'User-specified percentage reduction input driving the tech & software what-if program.',
    reconciliationRule: 'Server-validated scenario parameter, echoed back on the governed program run.',
    source: 'apps/api/src/decision-modules/sga-spend/sga-spend.tech-software-emission.ts#metricKey:sga_spend_tech_software_reduction_input_pct',
    ownerModuleId: 'sga-spend',
    unit: 'percent',
    frame: 'scenario_input_pct',
    temporalType: 'ratio',
    direction: 'context',
    blendPolicy: { kind: 'never_blend' },
    sourceOfTruthClass: 'derived',
    sourceBindingKeys: ['sga_spend_tech_software_reduction_input_pct'],
    benchmarkable: false,
  },
];

export const SGA_SPEND_EXISTING_REGISTRY_SEED_INPUTS: readonly MetricEntrySeedInput[] =
  Object.freeze([...SGA_SPEND_LEVER_SUBMODULE_SEED_INPUTS, ...SGA_SPEND_MODULE_AGGREGATE_SEED_INPUTS]);
